// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';
// @ts-expect-error: Incorrect type
import InfiniteScroller from '@zestia/ember-simple-infinite-scroller/components/infinite-scroller';
import scrollPosition from 'lumen/modifiers/scroll-position';

const ITEMS_TO_ADD = 15;

type ListItemComponent = ComponentLike<{
  Args: {
    item: unknown;
    maxValues: unknown;
    params: unknown;
    scrollDownOnItemExpand: () => void;
    scrollToTop: () => void;
  };
}>;

interface HotelAndMarketRevenueInsightBlockListSignature {
  Args: {
    items: unknown[];
    listItemComponent: ListItemComponent;
    maxValues: unknown;
    onListScroll?: (overlayVisibility: {
      hasItemsAboveViewport: boolean;
      hasItemsBelowViewport: boolean;
    }) => void;
    params: unknown;
  };
}

export default class HotelAndMarketRevenueInsightBlockList extends Component<HotelAndMarketRevenueInsightBlockListSignature> {
  declare elScrollContainer: HTMLElement | null;

  itemsShown = ITEMS_TO_ADD;

  @computed('args.items.length', 'itemsShown')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get data() {
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/slice
    return this.args.items.slice(0, this.itemsShown);
  }

  willDestroy(): void {
    super.willDestroy();

    this.elScrollContainer = null;
  }

  @action
  didInsertBlockList(element: HTMLElement): void {
    this.elScrollContainer = element;
  }

  @action
  handleOnScrollEvent(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    const { scrollTop, clientHeight, scrollHeight } = target;
    this.args.onListScroll?.({
      hasItemsBelowViewport: scrollTop + clientHeight < scrollHeight,
      hasItemsAboveViewport: scrollTop > 0,
    });
  }

  @action
  loadMore(): void {
    const { itemsShown } = this;
    const dataLength = this.args.items?.length;

    // Only add items if you show less items then there is data
    // Otherwise you may decrease the itemshown without knowing
    // This causes issues with the expanding
    if (itemsShown < dataLength) {
      // If we've reached the end, don't add to many elements
      const newItemsShown = Math.min(dataLength, itemsShown + ITEMS_TO_ADD);

      set(this, 'itemsShown', newItemsShown);
    }
  }

  @action
  scrollDownOnItemExpand(): void {
    if (this.elScrollContainer) {
      // We scroll down a bit so the user can see some of the expanded items
      setTimeout(() => {
        this.elScrollContainer?.scroll({
          top: 120,
          behavior: 'smooth',
        });
      }, 0);
    }
  }

  @action
  scrollToTop(): void {
    if (this.elScrollContainer) {
      // We scroll down a bit so the user can see some of the expanded items
      this.elScrollContainer.scroll({
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  <template>
    <InfiniteScroller
      @onLoadMore={{this.loadMore}}
      @percent={{85}}
      class="block-list-infinite-scroller block-list"
      {{didInsert this.didInsertBlockList}}
      {{scrollPosition this.handleOnScrollEvent}}
    >
      {{#each this.data as |item|}}
        {{#let
          (component (ensureSafeComponent @listItemComponent this))
          as |ListItem|
        }}
          <ListItem
            @item={{item}}
            @maxValues={{@maxValues}}
            @params={{@params}}
            @scrollDownOnItemExpand={{this.scrollDownOnItemExpand}}
            @scrollToTop={{this.scrollToTop}}
          />
        {{/let}}
      {{/each}}
    </InfiniteScroller>
  </template>
}
